import { FiArrowDown, FiArrowUp } from "react-icons/fi";
import type { TableColumn } from "../config";

type SortField = "filename" | "audioText" | "createdAt";
type SortOrder = "asc" | "desc";

interface TableHeaderProps {
  /** Columns to render */
  columns: TableColumn[];
  /** Current sort field */
  sortField: SortField;
  /** Current sort order */
  sortOrder: SortOrder;
  /** Whether all items are selected */
  allSelected: boolean;
  /** Whether some items are selected */
  someSelected: boolean;
  /** Callback for select all */
  onSelectAll: () => void;
  /** Callback for sort change */
  onSort: (field: SortField) => void;
}

/**
 * TableHeader - Dynamic table header based on columns config
 *
 * Renders sortable headers for supported columns.
 */
export function TableHeader({
  columns,
  sortField,
  sortOrder,
  allSelected,
  someSelected,
  onSelectAll,
  onSort,
}: TableHeaderProps) {
  // Get header text and sortable status for each column
  const getColumnConfig = (column: TableColumn) => {
    switch (column) {
      case "checkbox":
        return { label: "", sortable: false, sortKey: null };
      case "preview":
      case "filename":
        return { label: "File", sortable: true, sortKey: "filename" as SortField };
      case "audioText":
        return { label: "Audio Text", sortable: true, sortKey: "audioText" as SortField };
      case "tags":
        return { label: "Tags", sortable: false, sortKey: null };
      case "url":
        return { label: "URL", sortable: false, sortKey: null };
      case "actions":
        return { label: "Actions", sortable: false, sortKey: null };
      case "duration":
        return { label: "Duration", sortable: false, sortKey: null };
      default:
        return { label: "", sortable: false, sortKey: null };
    }
  };

  const renderHeader = (column: TableColumn) => {
    const config = getColumnConfig(column);

    // Skip filename if preview is present (they're combined)
    if (column === "filename" && columns.includes("preview")) {
      return null;
    }

    // Checkbox column
    if (column === "checkbox") {
      return (
        <th key="checkbox" className="w-12 px-4 py-3 text-left">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(input) => {
              if (input) {
                input.indeterminate = someSelected;
              }
            }}
            onChange={onSelectAll}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            aria-label="Select all media"
          />
        </th>
      );
    }

    // Actions column (right-aligned)
    if (column === "actions") {
      return (
        <th
          key="actions"
          className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
        >
          {config.label}
        </th>
      );
    }

    // Sortable columns
    if (config.sortable && config.sortKey) {
      return (
        <th
          key={column}
          className="cursor-pointer px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-100"
          onClick={() => config.sortKey && onSort(config.sortKey)}
        >
          <div className="flex items-center gap-2">
            <span>{config.label}</span>
            {sortField === config.sortKey &&
              (sortOrder === "asc" ? (
                <FiArrowUp className="h-3 w-3" />
              ) : (
                <FiArrowDown className="h-3 w-3" />
              ))}
          </div>
        </th>
      );
    }

    // Non-sortable columns
    return (
      <th
        key={column}
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
      >
        {config.label}
      </th>
    );
  };

  return (
    <thead className="bg-gray-50">
      <tr>{columns.map(renderHeader)}</tr>
    </thead>
  );
}

export default TableHeader;
